import React, { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Avatar,
    Divider,
    Paper,
    Alert,
    TextField,
    Button,
    CircularProgress
} from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import client from "../api/client";
import { useCreatePurchaseMutation } from "../api/mutations/purchase/create-purchase-mutation";
import { useNavigate } from "react-router-dom";

// No Money Back & Secure Processing (VERTICAL)
function InfoHighlights() {
    return (
        <Box sx={{ my: 2 }}>
            <Box display="flex" alignItems="center" mb={2}>
                    <Avatar
                    src="/assets/no-money-back.png"
                    sx={{ width: { xs: 64, sm: 80 }, height: { xs: 64, sm: 100 }, mr: 2 }}
                    variant="rounded"
                />
                <Box>
                    <Typography variant="h6" >
                        No Money Back
                    </Typography>
                    <Typography variant="body2" >
                        Due to the nature of digital products, all ebook purchases are final and non-refundable. Once the file is delivered, you instantly receive full access to the content, so we’re unable to offer cancellations or refunds.
                    </Typography>
                </Box>
            </Box>
            <Box display="flex" alignItems="center">
                <Avatar
                    src="/assets/secure-processing.png"
                    sx={{ width: { xs: 56, sm: 70 }, height: { xs: 56, sm: 100 }, mr: 2 }}
                    variant="rounded"
                />
                <Box>
                    <Typography variant="h6" >
                        SECURE PROCESSING
                    </Typography>
                    <Typography variant="body2" >
                        All orders go through a secure network. Your credit card information is never stored in any way. We respect your privacy.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

// Headline, Image, Price, and Features List
function ProductDetails() {
    return (
        <Box sx={{ my: 4 }}>
            <Typography variant="h5"  fontWeight={400} gutterBottom>
                <strong>Craft Your Journey: A Parental Guide For The Positive Psychology Kids' Program (PPKP)</strong>
            </Typography>
            <Box sx={{ my: 2 }}>
                <img
                    src="/assets/cyj-ebook-chapter-bundle.png"
                    alt="Ebook"
                    style={{ width: "100%", maxWidth: 400, borderRadius: 8 }}
                />
            </Box>
            <Typography variant="subtitle1"  color="primary" fontWeight={700}>
                INSTANT ACCESS FOR JUST
            </Typography>
            <Typography variant="h6"  color="primary" fontWeight={700}>
                $37 EBook + Bonuses
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1"  fontWeight={700} gutterBottom>
                <u>HERE'S WHAT YOU'LL HAVE ACCESS TO:</u>
            </Typography>
            <Box component="ul" sx={{ fontSize: { xs: 14, sm: 18 }, pl: 3, pr: { xs: 1, sm: 0 }, wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                <Box component="li">
                    <strong>A full 166-page Positive Psychology guide</strong> to raising joyful, resilient, and hopeful kids — filled with science-based insights, real stories, and practical exercises.
                </Box>
                <Box component="li">
                    <strong>Posters &amp; Visual Tools</strong> &nbsp; that help children explore emotions, strengths, kindness, and a growth mindset.
                </Box>
                <Box component="li">
                    <strong>Hands-on Family Activities</strong> like the Gratitude Jar, Family Treasure Box, and Kindness Challenges — turning everyday moments into bonding rituals.
                </Box>
                <Box component="li">
                    <strong>Lots of Guidance + Conversation-Starter Prompts for parents</strong> — helping you talk, connect, and understand your child on a deeper level.
                </Box>
                <Box component="li">
                    <strong>Games, Planners &amp; Routine Charts</strong> that make emotional learning fun and easy to weave into daily life.
                </Box>
                <Box component="li">
                    <strong>Bonus Printable</strong> — including Mother–Child Time Posters, an Anger Management Mini Pack, a Weekly Routine Planner, and a Reward Chart to celebrate effort and progress.
                </Box>
                <Box component="li">
                    <strong>End-of-Program Certificate</strong> — a special keepsake to celebrate your child’s growth and the family’s shared journey.
                </Box>
                <Box component="li">
                    <strong>Reflective Prompts</strong> that help you pause, breathe, and parent with calm intention.
                </Box>
            </Box>
        </Box>
    );
}

// Testimonial (with icon instead of image)
function Testimonial() {
    return (
        <Box sx={{ my: 3 }}>
            <Box display="flex" alignItems="flex-start" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', pr: { xs: 1, sm: 0 } }}>
                <FormatQuoteIcon sx={{ fontSize: 40, color: "primary.main", mr: 2, mt: 0.5 }} />
                <Box>
                    <Typography fontSize={17} sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                        S. had an amazing time in the workshops.<br />
                        And I hope you can do the second one, maybe this year she would attend it if you are back in Lebanon.<br /><br />
                        <strong>I advice it to everybody, to all of my friends.</strong> It is a very good supportive sessions for the kids, for the psychology <strong>to enhance their way of thinking, to give them more support, more self confidence.</strong><br /><br />
                        Thank you so much and it would be great if we can attend the 2nd one soon the 2nd level. Thank you<br /><br />
                        <strong>S.F</strong>
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function OrderForm() {
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const amountUsd = 37;
    
    const { mutateAsync: createPurchase } = useCreatePurchaseMutation();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Add validation for PayPal form fields
    const isPaypalFormValid = form.firstName && form.lastName && form.email && form.phone && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && /^[0-9+\s-]+$/.test(form.phone);

    // Single-step flow; customer details appear only when PayPal is selected

    // Get PayPal client ID from environment variable
    const paypalClientId = "ATnw78Oayf8yprroSiVf76tpwcYqe-TY_CbVSvJmPrVedNkhN0AJzsxWzzqMo5nFBMXa2eRSO9lCMoBy";
    return (
        <Box sx={{ bgcolor: "background.default", p: { xs: 2, sm: 5 } }}>
            <Paper
            elevation={3}
            sx={{
                bgcolor: "white",
            }}
            >
            <Grid container spacing={{ xs: 2, sm: 4 }} sx={{ mt: { xs: 1, sm: 2 }, ml: { xs: 0, md: 2, lg: 1 }, mx: "auto" }}>
                <Grid item xs={12} md={7}>
                    <ProductDetails />
                    <Testimonial />
                </Grid>
                <Grid item xs={12} md={5} paddingRight={{ xs: 0, md: 2 }}>
                    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 4 }, bgcolor: "#fff", borderRadius: 2, boxShadow: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Checkout
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {
                            <>
                                <Typography variant="h6" gutterBottom>
                                    Select Payment Method
                                </Typography>
                                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mt: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant={paymentMethod === 'PAYPAL' ? 'contained' : 'outlined'}
                                        color="primary"
                                        onClick={() => setPaymentMethod('PAYPAL')}
                                    >
                                        PayPal/Credit, Debit Card
                                    </Button>
                                    <Button
                                        variant={paymentMethod === 'WHISH' ? 'contained' : 'outlined'}
                                        color="primary"
                                        onClick={() => setPaymentMethod('WHISH')}
                                    >
                                        Whish/OMT
                                    </Button>
                                </Box>

                                {paymentMethod === 'PAYPAL' && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            Customer Details
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="First Name"
                                                    name="firstName"
                                                    value={form.firstName}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    margin="normal"
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Last Name"
                                                    name="lastName"
                                                    value={form.lastName}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    margin="normal"
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                        <TextField
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            fullWidth
                                            margin="normal"
                                            required
                                            placeholder="yourname.lastname@example.com"
                                            sx={{ bgcolor: '#fff' }}
                                            inputProps={{ 'aria-label': 'email', style: { color: '#000' } }}
                                        />
                                        <TextField
                                            label="Phone Number (with country code)"
                                            name="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={handleChange}
                                            fullWidth
                                            margin="normal"
                                            placeholder="+961 ..."
                                            sx={{ bgcolor: '#fff' }}
                                            inputProps={{ 'aria-label': 'phone', style: { color: '#000' } }}
                                        />

                                        <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "USD", components: "buttons", "enable-funding": "card", "disable-funding": "sepa,paylater,venmo" }}>
                                            <Box sx={{ my: { xs: 1.5, sm: 2 } }}>
                                                <Typography variant="body1" sx={{ mb: 1 }}>
                                                    Amount: <strong>${amountUsd.toFixed(2)} USD</strong>
                                                </Typography>
                                                <PayPalButtons
                                                    fundingSource="paypal"
                                                    style={{ layout: 'vertical', shape: 'rect' }}
                                                    createOrder={async (data, actions) => {
                                                        const orderId = await actions.order.create({
                                                            purchase_units: [
                                                                {
                                                                    amount: { value: amountUsd.toFixed(2) },
                                                                    description: "Craft Your Journey: PPKP Ebook + Bonuses",
                                                                },
                                                            ],
                                                        });
                                                        try { console.log('PayPal createOrder -> orderId:', orderId); } catch {}
                                                        return orderId;
                                                    }}
                                                    onApprove={(data, actions) => {
                                                        setLoading(true);
                                                        return actions.order.capture()
                                                            .then(async (details) => {
                                                                try {
                                                                    console.log('PayPal onApprove -> data:', data);
                                                                    console.log('PayPal capture details:', details);
                                                                } catch {}
                                                                try {
                                                                    const paypalOrderId = data?.orderID || details?.id;
                                                                    await createPurchase({
                                                                        email: form.email.trim(),
                                                                        firstName: form.firstName.trim(),
                                                                        lastName: form.lastName.trim(),
                                                                        phone: form.phone.trim() || undefined,
                                                                        amount: amountUsd,
                                                                        currency: 'USD',
                                                                        paymentMethod: 'PAYPAL',
                                                                        paypalOrderId,
                                                                    });
                                                                } catch (e) {
                                                                    console.warn('Failed to create purchase in backend after approval', e);
                                                                }
                                                            })
                                                            .then(async () => {
                                                                setLoading(false);
                                                                const Swal = (await import('sweetalert2')).default;
                                                                Swal.fire({ title: 'Payment Successful', text: 'Your ebook will be emailed shortly.', icon: 'success', confirmButtonText: 'OK' })
                                                                    .then((result) => { if (result.isConfirmed) navigate('/thank-you'); });
                                                            })
                                                            .catch(async (err) => {
                                                                setLoading(false);
                                                                const Swal = (await import('sweetalert2')).default;
                                                                Swal.fire({ title: 'Payment Issue', text: 'We could not confirm your payment. Please try again or contact support.', icon: 'error' });
                                                                setError('Payment could not be completed. Please try again.');
                                                            });
                                                    }}
                                                    onError={(err) => {
                                                        const details = err?.message ? ` (${err.message})` : "";
                                                        setError(`PayPal payment could not be completed${details}. Please try again.`);
                                                    }}
                                                    disabled={!isPaypalFormValid || loading}
                                                />
                                                <Box sx={{ mt: 1.2 }}>
                                                    <PayPalButtons
                                                        fundingSource="card"
                                                        style={{ layout: 'vertical', shape: 'rect' }}
                                                        createOrder={async (data, actions) => {
                                                            const orderId = await actions.order.create({
                                                                purchase_units: [
                                                                    {
                                                                        amount: { value: amountUsd.toFixed(2) },
                                                                        description: "Craft Your Journey: PPKP Ebook + Bonuses",
                                                                    },
                                                                ],
                                                            });
                                                            try { console.log('PayPal createOrder -> orderId:', orderId); } catch {}
                                                            return orderId;
                                                        }}
                                                        onApprove={(data, actions) => {
                                                            setLoading(true);
                                                            return actions.order.capture()
                                                                .then(async (details) => {
                                                                    try {
                                                                        console.log('PayPal onApprove -> data:', data);
                                                                        console.log('PayPal capture details:', details);
                                                                    } catch {}
                                                                    try {
                                                                        const paypalOrderId = data?.orderID || details?.id;
                                                                        await createPurchase({
                                                                            email: form.email.trim(),
                                                                            firstName: form.firstName.trim(),
                                                                            lastName: form.lastName.trim(),
                                                                            phone: form.phone.trim() || undefined,
                                                                            amount: amountUsd,
                                                                            currency: 'USD',
                                                                            paymentMethod: 'PAYPAL',
                                                                            paypalOrderId,
                                                                        });
                                                                    } catch (e) {
                                                                        console.warn('Failed to create purchase in backend after approval', e);
                                                                    }
                                                                })
                                                                .then(async () => {
                                                                    setLoading(false);
                                                                    const Swal = (await import('sweetalert2')).default;
                                                                    Swal.fire({ title: 'Payment Successful', text: 'Your ebook will be emailed shortly.', icon: 'success', confirmButtonText: 'OK' })
                                                                        .then((result) => { if (result.isConfirmed) navigate('/thank-you'); });
                                                                })
                                                                .catch(async (err) => {
                                                                    setLoading(false);
                                                                    const Swal = (await import('sweetalert2')).default;
                                                                    Swal.fire({ title: 'Payment Issue', text: 'We could not confirm your payment. Please try again or contact support.', icon: 'error' });
                                                                    setError('Payment could not be completed. Please try again.');
                                                                });
                                                        }}
                                                        onError={(err) => {
                                                            const details = err?.message ? ` (${err.message})` : "";
                                                            setError(`Card payment could not be completed. Please try again, or use PayPal login instead${details}.`);
                                                        }}
                                                        disabled={!isPaypalFormValid || loading}
                                                    />
                                                </Box>
                                            </Box>
                                        </PayPalScriptProvider>
                                    </>
                                )}

                                {paymentMethod === 'WHISH' && (
                                    <Box sx={{ my: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="body1" sx={{ mb: 1 }}>
                                            Amount: <strong>${amountUsd.toFixed(2)} USD</strong>
                                        </Typography>
                                        <Button
                                            fullWidth
                                            size="large"
                                            sx={{ height: 48, borderRadius: 1.5 }}
                                            variant="contained"
                                            color="primary"
                                            onClick={async () => {
                                                const Swal = (await import('sweetalert2')).default;
                                                Swal.fire({
                                                    title: 'Pay via Whish or OMT',
                                                    html: `Please send the amount of <strong>$${amountUsd.toFixed(2)} USD</strong> via Whish or OMT to this number: <strong>+12108128269</strong><br/><br/>Name: <strong>Sara Hafez</strong><br/>After payment, send us a message either by email at <strong>hello@sarasjournal.com</strong> or on WhatsApp at <strong>+12108128269</strong> with your full name, email, phone number, and a screenshot or picture of the Whish/OMT receipt. You should receive your ebook within 24 hours. Please send us a message otherwise...`,
                                                    icon: 'info',
                                                    confirmButtonText: 'Got it',
                                                })
                                             
                                            }}
                                        >
                                            Pay via Whish or OMT
                                        </Button>
                                    </Box>
                                )}

                                <Box sx={{ mt: 3, mb: 2, p: 2, bgcolor: 'custom.blue', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Join Our Community WhatsApp Group!
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Connect with other parents, get tips, and stay updated with the latest from Sara's Journal.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        bgcolor="custom.blue"
                                        color="success"
                                        href="https://chat.whatsapp.com/H2aqVIWLJQu1BkIA664xMV?mode=hqrc"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ mt: 1 }}
                                    >
                                        Join Community WhatsApp
                                    </Button>
                                </Box>

                                {loading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <CircularProgress />
                                    </Box>
                                )}

                                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                    All payments are securely processed through PayPal. You will be prompted to sign in to your PayPal account (or pay as a guest when available).
                                </Typography>
                                <InfoHighlights />
                            </>
                        }
                    </Box>
                </Grid>
            </Grid>
            </Paper>
        </Box>
    );
}

export default OrderForm;
